import {ModalContextProvider} from './useModalContext';
import {CSSStyles} from '../../../types/CSSStyles';
import useAppTheme from '../../design_system/hooks/useAppTheme';
import useMediaQueryGetter, {
  MediaQuery,
} from '../../design_system/hooks/useMediaQueryGetter';
import {WRAPPER_Z_INDEX} from '../../Image/PanAndZoomImageCanvas';
import Flex from '../flex/Flex';
import useWindowScrollDiabler from '../useWindowScrollDisabler';

import {css} from '@emotion/react';
import React, {useEffect, useId} from 'react';

const CLOSE_BUTTON_HEIGHT = 48;

export interface Props {
  readonly onClose: () => void;
  readonly shown: boolean;
  readonly children?: React.ReactNode;
  readonly contentSX?: CSSStyles;
}

/**
 * @deprecated Use the Modal from src/components directory
 */
export default function Modal(props: Props): JSX.Element | null {
  const {children, contentSX, onClose, shown} = props;
  const windowScrollDisabler = useWindowScrollDiabler();
  const {sizes, spacing} = useAppTheme();
  const mq = useMediaQueryGetter();

  const id = useId();
  useEffect(() => {
    if (shown) {
      windowScrollDisabler.addKey(id);
    } else {
      windowScrollDisabler.removeKey(id);
    }
    return () => {
      windowScrollDisabler.removeKey(id);
    };
  }, [id, shown, windowScrollDisabler]);

  if (!shown) {
    return null;
  }

  return (
    <Flex
      css={css({
        justifyContent: 'center',
        position: 'fixed',
        paddingLeft: spacing.x20,
        paddingRight: spacing.x20,
        top: 0,
        left: 0,
        height: '100vh',
        width: '100vw',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        paddingBottom: 20,
        zIndex: WRAPPER_Z_INDEX * 10,

        paddingTop: sizes.l.topNavHeight + CLOSE_BUTTON_HEIGHT / 2,
        [mq(MediaQuery.SmallScreen)]: {
          paddingTop: sizes.sm.topNavHeight + CLOSE_BUTTON_HEIGHT / 2,
        },
        [mq(MediaQuery.MediumScreen)]: {
          paddingTop: sizes.md.topNavHeight + CLOSE_BUTTON_HEIGHT / 2,
        },
      })}
    >
      <div
        css={css({
          cursor: 'pointer',
          position: 'fixed',
          height: '100vh',
          width: '100vw',
        })}
        role="button"
        onMouseUp={onClose}
        onTouchEnd={onClose}
      />
      <Flex
        css={[
          css({
            boxSizing: 'border-box',
            position: 'relative',
            height: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }),
          contentSX,
        ]}
        direction="column"
      >
        <div css={css({pointerEvents: 'auto'})}>
          <ModalContextProvider onClose={onClose}>
            {children}
          </ModalContextProvider>
        </div>
      </Flex>
    </Flex>
  );
}
